import React, { useEffect, useRef } from "react";
import { TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { TurnrText } from "~/components/TurnrText.jsx";
import { CountryFlags } from "~/components/CountryFlag.jsx";
import styles from "./PriceSetupCard.module.scss";
import ShipmentMethod from "~/components/ShipmentMethod.jsx";
import MoneyWithCurrency from "~/components/MoneyWithCurrency.jsx";
import { COLORS } from "rylak-common/src/constants";
import { ENTITY_TYPE, TOOLTIP_TEXTS } from "~/constants";
import { TurnrTooltip, TurnrTooltipPortalAnchor } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import { SHIPPING_METHOD_MAP } from "~/components/chips/statusChips/ShippingMethodChip.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import { isComplaintsDisabled, isExchangeDisabled, isExchangesComplaintDisabled } from "~/cores/priceSetupCore";
import { CHIP_COLOR_TYPES, CustomChip } from "~/components/chips/CustomChip.jsx";
import { getServiceTypeConfig, isServiceType } from "~/components/chips/statusChips/ServiceTypeChip.jsx";
import { getExchangePolicyDescription, getExchangePolicyLabel, getProductCategoryLabelFromId, getSelectedBrandId } from "~/cores/appCore";
import { useServiceHook } from "rylak-common";
import { fetchEntitiesAll } from "~/services";
import AvatarAndItemName, { ItemAvatar } from "~/components/items/AvatarAndItemName.jsx";
import { equalAsStringOrNumber } from "~/utils";
import { getProductId, getProductImage } from "~/cores/productCore";
import { EntityCardPriorityHeader } from "~/components/entityAction/EntityListCards.js";

export function PreConditionComponentsWrapper({ children }) {
    return <div className={styles["categories"]}>{children}</div>;
}

export function PreConditionComponent({ preConditionList, title, labelFn = (preCondition) => preCondition }) {
    return preConditionList?.map((preCondition) => {
        return <CustomChip key={preCondition} tooltipTitle={title} size={"small"} colorType={CHIP_COLOR_TYPES.DONE} disableTooltip={false} label={labelFn(preCondition)}></CustomChip>;
    });
}

export function PreConditionComponentProductCategories({ appProps, preConditionList, productCategoryIds = preConditionList }) {
    return <PreConditionComponent preConditionList={productCategoryIds} title={"Product Category"} labelFn={(id) => getProductCategoryLabelFromId(appProps, { id })} />;
}

export function PreConditionComponentProducts({ appProps, productIds = [] }) {
    const { serviceData: productsServiceData, fetchServiceData: fetchProductsServiceData } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.PRODUCT,
            merchantBrandId: getSelectedBrandId(appProps),
        },
        fetchFn: fetchEntitiesAll,
    });

    useEffect(() => {
        if (productIds?.length > 0) {
            fetchProductsServiceData({ ids: productIds?.join(",") });
        }
    }, [productIds]);

    if (!productIds?.length) return null;

    return (
        <div className={styles["products"]}>
            {productIds.map((id) => {
                const product = productsServiceData?.data?.find((p) => equalAsStringOrNumber(getProductId(p), id));
                return (
                    <TurnrTooltip key={id} content={<AvatarAndItemName productVariant={product} />}>
                        <ItemAvatar imageSize={30} imageClickable={false} productVariant={product} imageUrl={!getProductImage(product) && "/static/images/image-load-failed.png"} />
                    </TurnrTooltip>
                );
            })}
        </div>
    );
}

export default function PriceSetupCard(props) {
    const { priceSetup, appProps } = props;

    const countries = priceSetup?.countries?.length ? priceSetup?.countries : ["GLOBAL"];
    const postCodes = priceSetup?.postCodes;
    const orderTags = priceSetup?.orderTags;
    const provinces = priceSetup?.provinces;
    const productCategoryIds = priceSetup?.productCategoryIds || [];
    const productIds = priceSetup?.productIds || [];

    const currencyCode = priceSetup?.currencyCode;

    const isStandard = !isServiceType(priceSetup?.serviceType);

    const returnTimeLimit = priceSetup?.returnTimeLimit;
    // Service flows never charge a return handling cost, so it must not be folded into the shipment
    // option totals below. The row is hidden for them in both places that would show it: the card
    // body and the per-shipment-option tooltip.
    const returnHandlingCost = (isStandard ? priceSetup?.returnHandlingCost : 0) || 0;
    const failedDeliveryCost = priceSetup?.failedDeliveryCost || 0;
    const exchangePolicy = priceSetup?.exchangePolicy;

    const tooltipPortalRef = useRef();

    const exchangeDisabled = isExchangeDisabled(priceSetup);
    const exchangesDisabledForComplaints = isExchangesComplaintDisabled(priceSetup);

    const complaintsDisabled = isComplaintsDisabled(priceSetup);

    return (
        <TurnrTooltipPortalAnchor ref={tooltipPortalRef} placement={"top-left"}>
            <div className={styles["card"]} style={{ height: "100%", overflow: "visible" }}>
                <EntityCardPriorityHeader entity={priceSetup} priorityTooltipPortalRef={tooltipPortalRef}>
                    <div style={{ marginRight: "30px" }}>
                        <CountryFlags countryCodes={countries} parsedCountries={priceSetup?.parsedCountries} tooltipPortalRef={tooltipPortalRef} />
                    </div>
                    <PreConditionComponentsWrapper>
                        <PreConditionComponent preConditionList={provinces} title={"Province"} />
                        <PreConditionComponent preConditionList={postCodes} title={"Postal Code"} />
                        <PreConditionComponent preConditionList={orderTags} title={"Order Tag"} />
                        <PreConditionComponentProductCategories appProps={appProps} productCategoryIds={productCategoryIds} />
                    </PreConditionComponentsWrapper>
                    <PreConditionComponentProducts appProps={appProps} productIds={productIds} />
                </EntityCardPriorityHeader>
                <div style={{ marginTop: "auto" }}>
                    <LineDivider />
                    <TurnrCardContent>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "auto 1fr",
                                gridColumnGap: "10px",
                                gridRowGap: "15px",
                                marginTop: "10px",
                                marginBottom: "10px",
                                alignItems: "center",
                            }}
                        >
                            {!isStandard && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        Service type:{" "}
                                    </TurnrText>
                                    <div style={{ width: "fit-content" }}>
                                        <CustomChip label={getServiceTypeConfig(priceSetup?.serviceType).label} size={"small"} colorType={CHIP_COLOR_TYPES.PREMIUM} disableTooltip={true} />
                                    </div>
                                </>
                            )}

                            {isStandard && returnTimeLimit && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        Return time limit:{" "}
                                    </TurnrText>
                                    <div>
                                        <div style={{ width: "fit-content" }}>
                                            <TurnrTooltip portalRef={tooltipPortalRef} title={"Return handling cost"} content={TOOLTIP_TEXTS.PRICE_SETUP_RETURN_HANDLING_COST}>
                                                <MoneyWithCurrency money={returnTimeLimit} currencyCode={"days"} />
                                            </TurnrTooltip>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isStandard && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        Return handling cost:{" "}
                                    </TurnrText>
                                    <div>
                                        <div style={{ width: "fit-content" }}>
                                            <TurnrTooltip portalRef={tooltipPortalRef} title={"Return handling cost"} content={TOOLTIP_TEXTS.PRICE_SETUP_RETURN_HANDLING_COST}>
                                                <MoneyWithCurrency money={returnHandlingCost} currencyCode={currencyCode} />
                                            </TurnrTooltip>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isStandard && failedDeliveryCost > 0 && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        Failed delivery cost:{" "}
                                    </TurnrText>
                                    <div>
                                        <div style={{ width: "fit-content" }}>
                                            <TurnrTooltip portalRef={tooltipPortalRef} title={"Failed delivery cost"} content={TOOLTIP_TEXTS.PRICE_SETUP_FAILED_DELIVERY_COST}>
                                                <MoneyWithCurrency money={failedDeliveryCost} currencyCode={currencyCode} />
                                            </TurnrTooltip>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isStandard && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        {exchangeDisabled ? "Exchanges" : "Exchange cost"}:{" "}
                                    </TurnrText>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <div style={{ width: "fit-content" }}>
                                            {exchangeDisabled ? (
                                                <CustomChip
                                                    label={exchangesDisabledForComplaints ? "Disabled" : "Complaints only"}
                                                    size={"small"}
                                                    colorType={CHIP_COLOR_TYPES.WARNING}
                                                    disableTooltip={true}
                                                />
                                            ) : (
                                                <TurnrTooltip portalRef={tooltipPortalRef} title={"Exchange cost"} content={TOOLTIP_TEXTS.PRICE_SETUP_EXCHANGE_COST}>
                                                    <MoneyWithCurrency money={priceSetup?.exchangeCost} currencyCode={currencyCode} />
                                                </TurnrTooltip>
                                            )}
                                        </div>
                                        {(!exchangeDisabled || !exchangesDisabledForComplaints) && exchangePolicy && (
                                            <div>
                                                <TurnrTooltip
                                                    portalRef={tooltipPortalRef}
                                                    title={getExchangePolicyLabel(appProps, { id: exchangePolicy })}
                                                    content={getExchangePolicyDescription(appProps, { id: exchangePolicy })}
                                                >
                                                    <CustomChip
                                                        size={"small"}
                                                        colorType={CHIP_COLOR_TYPES.DONE}
                                                        disableTooltip={true}
                                                        label={getExchangePolicyLabel(appProps, { id: exchangePolicy })}
                                                    ></CustomChip>
                                                </TurnrTooltip>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {isStandard && complaintsDisabled && (
                                <>
                                    <TurnrText weak size={"sm"}>
                                        Complaints
                                    </TurnrText>
                                    <div style={{ width: "fit-content" }}>
                                        <CustomChip label={"Disabled"} size={"small"} colorType={CHIP_COLOR_TYPES.WARNING} disableTooltip={true} />
                                    </div>
                                </>
                            )}
                        </div>
                    </TurnrCardContent>
                    <LineDivider />
                    <TurnrCardContent style={{ marginTop: "15px" }}>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gridGap: "10px",
                            }}
                        >
                            {priceSetup?.shipmentSolutions?.map((shipmentSolution, index) => {
                                const totalCostComponent = <MoneyWithCurrency money={returnHandlingCost + (shipmentSolution?.cost || 0)} currencyCode={currencyCode} />;
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <TurnrTooltip portalRef={tooltipPortalRef} content={SHIPPING_METHOD_MAP[shipmentSolution?.type]?.tooltipTitle}>
                                            <div>
                                                <ShipmentMethod shipment={shipmentSolution} appProps={appProps} />
                                            </div>
                                        </TurnrTooltip>
                                        <TurnrTooltip
                                            portalRef={tooltipPortalRef}
                                            content={
                                                <div>
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "auto 1fr",
                                                            gridColumnGap: "10px",
                                                            gridRowGap: "5px",
                                                        }}
                                                    >
                                                        <TurnrText size={"sm"}>Total return cost:</TurnrText>
                                                        {totalCostComponent}

                                                        <LineDivider
                                                            className={"my1"}
                                                            style={{
                                                                gridColumn: "span 2",
                                                                backgroundColor: COLORS.WEAK_TEXT,
                                                            }}
                                                        />
                                                        {isStandard && (
                                                            <>
                                                                <TurnrText size={"sm"}>Return handling cost:</TurnrText>
                                                                <MoneyWithCurrency money={returnHandlingCost} currencyCode={currencyCode} />
                                                            </>
                                                        )}
                                                        <TurnrText size={"sm"}>Shipment method cost:</TurnrText>
                                                        <MoneyWithCurrency money={shipmentSolution?.cost} currencyCode={currencyCode} />
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <div
                                                style={{
                                                    marginLeft: "10px",
                                                }}
                                            >
                                                {totalCostComponent}
                                            </div>
                                        </TurnrTooltip>
                                    </div>
                                );
                            })}
                        </div>
                    </TurnrCardContent>
                </div>
            </div>
        </TurnrTooltipPortalAnchor>
    );
}
